/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { colors, sizes } from '@smartsheet/lodestar-core';
import { isObject } from 'lodash';
import { createRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from '../../../../../common/Column';
import KebabMenu from '../../KebabMenu/KebabMenu';
import { copyJSONSource, copyReference } from '../../data';
import { ObjectHeader } from './ObjectHeader';

const getValueColor = (value: string | number | boolean) => {
  const type = typeof value;
  switch (type) {
    case 'string':
      return colors.visTealDark10;
    case 'number':
      return colors.visPurpleDark10;
    case 'boolean':
      return colors.visOrangeDark10;
  }
};
const nameStyle = css`
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
`;

const refMenuContainerDisplayPropertyName =
  '--refMenuContainerDisplayPropertyName';
const rightColumnStyle = css`
  display: var(${refMenuContainerDisplayPropertyName}, none);
  justify-content: center;
`;
const keyValueContainerStyle = css`
  display: flex;
  margin-left: 61px;
  height: 28px;
  border-radius: 2px;
  :hover {
    background: ${colors.neutralLight20};
    ${refMenuContainerDisplayPropertyName}: flex;
    background-clip: content-box;
  }
  width: auto;
  justify-content: space-between;
  padding-right: ${sizes.large}px;
`;

const leftColumnStyle = css`
  width: calc(100% - ${sizes.xxLarge}px);
  overflow: hidden;
`;

const objectHeaderContainer = css`
  margin-left: 28px;
`;

const formatValue = (value: string | boolean | number) => {
  if (typeof value === 'string') {
    return value;
  }
  return value.toString();
};

export const Print = ({
  obj,
  path,
}: {
  obj: { [key: string]: any };
  path: string | undefined;
}) => {
  const { t: translate } = useTranslation();
  const refs = useRef([]);
  const entries = Object.entries(obj);

  refs.current = entries.map((_, i) => refs.current[i] ?? createRef());

  const els = entries.map(([a, b]: [string, any], i: number) => {
    if (isObject(b)) {
      return (
        <div css={objectHeaderContainer}>
          <ObjectHeader name={a} obj={b} path={`${path}.${a}`} key={i} />
        </div>
      );
    }

    const valueStyle = css`
      font-weight: 500;
      color: ${getValueColor(b)};
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `;

    return (
      <div css={keyValueContainerStyle} key={i}>
        <Column css={leftColumnStyle}>
          <span css={nameStyle}>{a}: </span>
          <span css={valueStyle}>{formatValue(b)}</span>
        </Column>
        <Column css={rightColumnStyle} width="28px">
          <KebabMenu
            id={`${a}-${i}`}
            items={[
              {
                title: translate('designer:runLog.kebabMenu.copyDataRef'),
                onClick: copyReference(`${path}.${a}`),
              },
              {
                title: translate('designer:runLog.kebabMenu.copyJSONSource'),
                onClick: copyJSONSource(b),
              },
            ]}
            menuArialabel={translate('designer:runLog.kebabMenu.menuArialabel')}
          />
        </Column>
      </div>
    );
  });
  return <div>{els}</div>;
};
